<template>
    <div>
        <button :class="buttonCss" id="buttonOpenModal" @click="openModal">
            <slot name="content"></slot>
            {{ buttonText }}
        </button>
        <modal
            v-if="showModal"
            root-id="loginModal"
            root-classes="modal show"
            modal-width="modal-md"
            :ignore-escape="true"
        >
            <template #modal-close>
                <div class="modal-close-container">
                    <button
                        id="modalClose"
                        type="button"
                        class="modal-close"
                        data-miss="#loginModal"
                        @click="closeModal"
                    >
                        <svg
                            width="25"
                            xmlns="http://www.w3.org/2000/svg"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 24 24"
                            role="img"
                            aria-label="Icono para cerrar mensaje"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            </template>
            <template #modal-container>
                <h3 class="text-center">Iniciar sesión</h3>
                <p class="text-center mt-2">Para acceder utiliza tus credenciales de</p>
                <p class="text-center font-semibold">Círculo Plus</p>
                <form
                    class="form-loader mt-6"
                    :class="{ active: loginButtonClicked }"
                    id="loginModalForm"
                    @submit.prevent="login"
                    @keydown="form.errors.clear($event.target.name)"
                >
                    <div
                        class="loader-container absolute bottom-0 left-0 right-0 top-0 w-full h-full flex flex-col items-center justify-center overflow-hidden z-2 bg-white opacity-85"
                    >
                        <div class="loader ease-linear rounded-full border-4 border-gray-300 h-12 w-12"></div>
                        <h2 class="text-center text-xl font-semibold mt-6">Cargando...</h2>
                        <p class="text-center">Se está procesando tu operación</p>
                    </div>
                    <div>
                        <label class="text-gray-500" for="email">Correo electrónico</label>
                        <input
                            id="email"
                            type="email"
                            name="email"
                            v-model="form.email"
                            class="block w-full py-3 mt-2 leading-5 transition duration-150 ease-in-out rounded form-input bg-teal-100 border border-teal-200 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:shadow-none sm:text-sm"
                            placeholder="Escribe tu correo electrónico"
                            required
                        />
                    </div>
                    <span
                        id="emailErrors"
                        v-if="form.errors.has('email')"
                        v-text="form.errors.get('email')"
                        class="text-red-500 text-sm"
                    ></span>
                    <div class="mt-4">
                        <label class="text-gray-500" for="password">Contraseña</label>
                        <password-toggle>
                            <input
                                id="password"
                                type="password"
                                name="password"
                                v-model="form.password"
                                class="block w-full py-3 mt-2 leading-5 transition duration-150 ease-in-out rounded form-input pr-11 bg-teal-100 border border-teal-200 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:shadow-none sm:text-sm"
                                autocomplete="off"
                                placeholder="Escribe tu contraseña"
                                required
                            />
                        </password-toggle>
                    </div>
                    <span
                        id="passwordErrors"
                        v-if="form.errors.has('password')"
                        v-text="form.errors.get('password')"
                        class="text-red-500 text-sm"
                    ></span>
                    <div class="relative flex items-start mt-5">
                        <div class="flex items-center h-5">
                            <input
                                id="remember"
                                name="remember"
                                type="checkbox"
                                class="w-4 h-4 text-red-500 transition duration-150 ease-in-out form-checkbox focus:shadow-none"
                            />
                        </div>
                        <div class="ml-3 text-sm leading-5">
                            <label
                                for="remember"
                                class="text-gray-500 transition duration-300 ease-in-out cursor-pointer hover:text-red-500"
                                >Recordarme</label
                            >
                        </div>
                    </div>
                    <button
                        id="loginButton"
                        type="submit"
                        class="btn btn-red block w-full px-10 py-3 mt-7"
                        :class="{ disabled: disableButton() }"
                    >
                        Iniciar sesión
                    </button>
                </form>
                <a
                    href="/forgot-password"
                    class="block text-sm text-center text-blue-500 transition duration-300 ease-in-out outline-none hover:text-blue-700 mt-5"
                    >¿Olvidaste tu contraseña?</a
                >
                <hr class="mt-8 mb-7 border-gray-200" />
                <div class="flex items-center flex-col">
                    <svg xmlns="http://www.w3.org/2000/svg" width="130" viewBox="0 0 2772.3 599">
                        <path
                            fill="#002f87"
                            d="M430 579.1l20 19.5 16.5-19.5h82.9l18.1-27.1H440.8l-3.6-4h-39.3l-2.3 4H17.8L0 579.1h430zM419.6 523.6h-52.1l-13.6-13.7H44l17.9-27.3h265.2l1-6h52.3l1.4 6h229.8l-18.2 27.3H409.1s1.2 7.1 10.5 13.7zM349.9 441.1h289.5l18-27.2H349.5l4.2-6.6h-92l-4.2 6.6H107.7l-17.9 27.2h196l12.4 11.7h61.2l-9.5-11.7zM384.4 369.2h302.4l18-27.3h-302l-2.2-5.8h-90.7l-3.4 5.8H155.1l-17.9 27.3h140.3s9.4 2.2 2.8 11.4h94.1l10-11.4zM433.8 299.8h297.7l18.1-27.2H459.5l-1.8-5.7h-100l-3.8 5.7H199.8l-17.9 27.2h143.9s7.4 3.5 2.2 10.7h101.3c.1.1.2-4.3 4.5-10.7zM479 241.5H376.1c5.7-9.3-8-10.9-8-10.9H226l17.7-27.3h157.9l3.9-6.2H509l4.5 6.2h280.1l-18.1 27.3H485.1s-4.4 4.7-6.1 10.9zM551.1 162.1h270.7l18-27.3H574.2l-6.7-11.8h-111l-8.5 11.8H290.2l-18 27.3h140.4s16.7.1 9.2 11.2h125.1c0 .1.5-3.8 4.2-11.2zM578.1 66.3h305.7l-18 27.2h-261c-7.3 0-12.3 11.4-12.3 11.4H468.9c6.3-10.9-7.5-11.4-7.5-11.4H316.3L334 66.3h228.5l9.6-8.8 23-31h-5.5L610.1 0h15.7l-19.1 26.5H602l-25 32 1.1 7.8zM1062.1 67.6l-71.8 107.9h-58.7l-49.5 73.7h61.6L768.2 513.1s-13.6 21-13.6 51.1c0 29.9 37.6 34.5 37.6 34.5h133.5l42.1-76.5h-66.1s-25.5-3-13.6-23.8c12.1-21.2 165.6-249.2 165.6-249.2h77.5l42.5-73.7h-70.9L1174 67.6h-111.9zM1478.7 598.6l339.6-531h-113.6l-339.4 531h113.4zM2432.8 598.6l339.5-531H2659l-339.6 531h113.4zM1426.7 426.2h-273.1s-27.8 34-31 63c0 0 0 23.9 29 23.9h210.1l-55.1 85.6h-171s-129.9 0-129.9-76.5c0 0-1.2-57.1 94-189.8 95.3-132.9 160.5-137.9 270-137.9s139.2 34.7 135.7 63.6c-3.6 32.9-7.4 50.2-78.7 168.1zm-226.5-74.4h180.1s20.1-34.5 26.2-63c6.2-28.5-26.8-36.1-63.7-36.1-36.7.1-93.1 3.9-142.6 99.1zM2370.9 426.2h-273.1s-27.9 34-30.9 63c0 0 0 23.9 28.9 23.9h210.3l-55.2 85.6h-171s-130 0-130-76.5c0 0-1.3-57.1 94.1-189.8 95.3-132.9 160.5-137.9 270-137.9s139 34.7 135.9 63.6c-3.9 32.9-7.7 50.2-79 168.1zm-226.4-74.4h180s20.2-34.5 26.2-63c6.1-28.5-27-36.1-63.7-36.1-36.7.1-93.1 3.9-142.5 99.1zM1707 489.2s0 23.9 28.8 23.9H1919l-55 85.6h-144s-140.1 4.4-129.9-76.5c0 0-1.5-57.1 93.8-189.8 0 0 86.3-137.9 272.4-137.9h123.1l-55.6 84.6h-99.1c-9.1 0-81 7.5-145.4 79.6-64.4 71.9-69.6 106.3-72.3 130.5zM2476.5 599l22.3-35h8.2l-6.8 29.5L2531 564h8l-22.3 35h-5.3l13.1-20.7c.6-.6 1.2-1.8 2.3-3.5 1.1-1.5 2.3-3.4 3.5-5.3l-30.7 29.5h-5.6l6.7-29.5-.7 1.1c-.6.8-1.5 2.1-2.5 3.9-1 1.7-2 3-2.4 3.7l-13.2 20.7h-5.4v.1zM2527 599l3.3-5.2h6l-3.3 5.2h-6zM2577.4 568.1h13.9c2 0 3.3.2 4.2.7 1.5.9 1.5 2.6 0 5s-3.3 4.1-5.5 4.9c-2.1.9-4.4 1.3-7.1 1.3h-13.1l7.6-11.9zm-19.7 30.9l9.5-15h13c2.3 0 3.8.2 4.7.6 1.4.8 1.4 2.3.1 4.5l-3.3 5.8c-.8 1.4-1.3 2.3-1.4 2.9-.2.4-.3.9-.3 1.1h6.9l.5-.8c-.6-.3-.9-1-.4-2.1.1-.6.6-1.7 1.4-2.9l2.8-4.8c1.2-2 1.6-3.4 1.4-4.3-.3-1-1-1.6-2.2-2.2 2.3-.9 4.4-1.9 6.3-3.2 1.9-1.3 3.5-3.1 4.8-5.1 2.6-4.2 2.4-6.8-.6-8.3-1.6-.9-4-1.2-7.2-1.2h-19.3l-22.3 35h5.6zM2595.2 599l3.4-5.2h5.9l-3.3 5.2h-6z"
                        ></path>
                    </svg>
                    <p class="text-gray-500 text-sm mt-6">
                        Si te diste de alta por medio de un SMS desde tu Telcel, puedes loguearte desde el botón
                        "Ingresar" de forma simple y directa.
                    </p>
                    <a href="/telcel/login" class="btn btn-telcel block w-full px-10 py-3 mt-5">Ingresar</a>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
import PasswordToggle from './PasswordToggle';
import Form from '../support/Form';
import sweetAlert from 'sweetalert2';
import Modal from './Modal';

export default {
    name: 'ModalLogin',
    components: {
        Modal,
        PasswordToggle,
    },

    props: {
        redirectUrl: String,
        buttonText: String,
        buttonCss: {
            type: String,
            default: 'btn btn-red md:inline-block block px-12 py-3 mt-6',
        },
    },

    data() {
        return {
            showModal: false,
            loginButtonClicked: false,
            form: new Form({
                email: '',
                password: '',
            }),
        };
    },

    computed: {
        inputsAreFilled() {
            if (!this.form.email) {
                return false;
            }

            if (!this.form.password) {
                return false;
            }

            return true;
        },
    },

    methods: {
        openModal() {
            this.showModal = true;
        },

        disableButton() {
            return !this.inputsAreFilled;
        },

        closeModal() {
            setTimeout(() => {
                $('body, html').removeClass('overflow-hidden');

                this.showModal = false;
                this.email = '';
                this.password = '';
            }, 500);
        },

        login() {
            this.loginButtonClicked = true;

            this.form
                .post('/login')
                .then(() => {
                    sweetAlert
                        .fire({
                            title: 'Éxito',
                            text: 'Se inició tu sesión correctamente.',
                            timer: 4000,
                            icon: 'success',
                            confirmButtonText: 'Cerrar',
                            buttonsStyling: false,
                            customClass: {
                                confirmButton: 'btn btn-green inline-block py-3 px-10',
                            },
                        })
                        .then(() => {
                            location.href = this.redirectUrl;
                        });
                })
                .catch(() => {
                    this.loginButtonClicked = false;
                    sweetAlert.fire('Error', 'Verifica tus datos.', 'error');
                });
        },
    },
};
</script>
